/* eslint-disable react/jsx-no-bind */
import { Grid, Typography } from '@material-ui/core';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Cell, Label, LabelList, ReferenceLine, Scatter, ScatterChart,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import StoryContext from '../../contexts/StoryContext';
import { rdsSelect } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toPercent } from '../../modules/text';
import ResponsiveContainer from '../ResponsiveContainer';
import ScatterTooltip from '../ScatterTooltip';
import NoDataVisual from '../NoDataVisual';

function PerformanceQuadrant() {
  const [, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [meanX, setMeanX] = useState(0);
  const [meanY, setMeanY] = useState(0);
  const [noData, setNoData] = useState(false);

  const { stateCode } = useParams();

  const { primaryColor } = useContext(StoryContext);

  useEffect(() => {
    setNoData(false);
    async function getData() {
      setError(null);

      function getTests() {
        const whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;

        return rdsSelect('3rdparty', 'cdc_lead', {
          cols: 'county_name,county_fips,count,tested_count,tested_pct',
          where: whereClause,
          format: 'amcharts',
          inject: false,
          limit: 200,
        });
      }

      function getBuildings() {
        const whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND (county_suppressed=0 OR county_suppressed=NULL)`;
        return rdsSelect('3rdparty', 'acs_sams_county_wide', {
          cols: 'county_fips,prop_struct_before_1980b',
          where: whereClause,
          format: 'amcharts',
          inject: false,
          orderby: 'county_fips',
        });
      }

      Promise.all([getTests(), getBuildings()])
        .then((results) => {
          // console.log(result);
          const median = (arr) => {
            const mid = Math.floor(arr.length / 2);
            const nums = [...arr].sort((a, b) => a - b);
            return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
          };

          const tests = results[0].parsedBody.dataProvider;
          const buildings = results[1].parsedBody.dataProvider;
          if (tests.length === 0) {
            setNoData(true);
            return;
          }
          const payload = tests.map((v) => {
            const countyBuildingData = _.find(buildings, { county_fips: v.county_fips });
            const dataPoint = { ...v };
            dataPoint.county = v.county_name.replace(' County', '');
            if (countyBuildingData) {
              dataPoint.percentBuiltBefore1979 = countyBuildingData.prop_struct_before_1980b;
            }
            return dataPoint;
          });

          const testsValues = payload.filter((d) => (d.percentBuiltBefore1979)).map((p) => (p.tested_pct));
          const buildingsValues = payload.filter((d) => (d.percentBuiltBefore1979)).map((p) => (p.percentBuiltBefore1979));

          setMeanX(median(buildingsValues));
          setMeanY(median(testsValues));

          // console.log(payload)
          setData(payload.filter((d) => (d.percentBuiltBefore1979)));
        })
        .catch((rdsError) => {
          setError(rdsError);
        })
        .finally(() => {
          setReady(true);
        });
    }
    if (stateCode && states[stateCode.toUpperCase()]) {
      // fetch actual average price data
      setReady(false);
      getData();
    }
  }, [stateCode]);

  const tooltipFormatter = function tooltipFormatter(payload) {
    const p = payload.payload;
    return `In <b>${p.county_name}</b>, <b>${toPercent(p.percentBuiltBefore1979)}</b> of houses were built before 1979 and 
    <b>${toPercent(p.tested_pct)}</b> of children had their blood lead levels tested.`;
  };

  if (noData) {
    return (
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
      >
        <Grid item xs={10} sm={6}>
          <NoDataVisual message="The Center for Disease Control does not have Blood Lead Level for this state." />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <Grid container>
        <Grid item xs={12} style={{ paddingRight: 10 }}>
          <ResponsiveContainer width="100%" aspect={1.7}>
            <ScatterChart>
              <XAxis
                dataKey="percentBuiltBefore1979"
                name="% of homes built before 1979"
                type="number"
                domain={['auto', 'auto']}
                tickFormatter={toPercent}
              >
                <Label value="% of homes built before 1979" offset={-5} position="insideBottom" fontSize="larger" />
              </XAxis>
              <YAxis
                dataKey="tested_pct"
                label={{
                  value: '% of children tested', angle: -90, position: 'insideLeft', fontSize: 'larger',
                }}
                name="% tested"
                type="number"
                domain={['auto', 'auto']}
                tickFormatter={toPercent}
              />
              <CartesianGrid />
              <ReferenceLine x={meanX} stroke={primaryColor} strokeDasharray="3 3">
                <Label position="insideTop" value="Median" />
              </ReferenceLine>
              <ReferenceLine y={meanY} stroke={primaryColor} strokeDasharray="3 3">
                <Label position="insideRight" value="Median" />
              </ReferenceLine>
              <Tooltip
                content={(
                  <ScatterTooltip
                    formatterFunction={tooltipFormatter}
                  />
                )}
              />
              <Scatter name="performance" data={data}>
                <LabelList dataKey="county" position="top" fill="#000" />
                {
                  data.map((entry, index) => {
                    let color = primaryColor;
                    if (entry.tested_pct < meanY && entry.percentBuiltBefore1979 > meanX) {
                      color = '#ff0000';
                    }
                    return <Cell key={`cell-${entry.county_name}`} fill={color} />;
                  })
                }
              </Scatter>

            </ScatterChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>
    </>
  );
}

export default PerformanceQuadrant;
